import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ChiefComplaint,
  ConsultationTitle,
  PatientInfoContent,
  VisitReasonChip,
} from 'widgets/home/PatientInfo.styles';
import { ResultCardShell } from 'widgets/resultCard/ResultCardShell';
import { formatReadableDate, formatReadableTime, formatRelativeDaySuffix } from 'utils';
import { type BriefResultData, type ExportStatus, ExportStatusValue } from 'types/result';
import { ConsultationInfo } from './ConsultationCard.styles';

const SECONDS_PER_MINUTE = 60;

interface ConsultationCardProps {
  result: BriefResultData;
  onStatusUpdate: (updatedStatus: ExportStatus, resultId: number) => void;
}

export const ConsultationCard = ({ result, onStatusUpdate }: ConsultationCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formattedDate = formatReadableDate(result.timestamp);
  const relativeSuffix = formatRelativeDaySuffix(result.timestamp);
  const heading = `${t('patientOverview.pastConsultations.heading', { date: formattedDate })}${relativeSuffix}`;
  const formattedDuration = `${Math.round(result.durationSeconds / SECONDS_PER_MINUTE)}min`;
  const status = result.exportStatus?.status;

  return (
    <ResultCardShell
      resultId={result.id}
      currentStatus={status}
      onStatusUpdate={(updatedStatus) => onStatusUpdate(updatedStatus, result.id)}
      onClick={() => void navigate(`/result/${result.id}`)}
      ariaLabel={`${heading}, Status: ${status ?? 'loading'}`}
      infoLine={`${formattedDate}${relativeSuffix} · ${formatReadableTime(result.timestamp)} · ${formattedDuration}`}
      isError={status === ExportStatusValue.Failure || status === ExportStatusValue.Error}
    >
      <ConsultationInfo>
        <PatientInfoContent>
          <ConsultationTitle>{heading}</ConsultationTitle>
          {result.chiefComplaint && <ChiefComplaint>{result.chiefComplaint}</ChiefComplaint>}
        </PatientInfoContent>
        {result.visitReasonShort && (
          <VisitReasonChip>{t(`home.visitReasons.${result.visitReasonShort}`)}</VisitReasonChip>
        )}
      </ConsultationInfo>
    </ResultCardShell>
  );
};
